import { Callback } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"

import { isMobile } from "../../../../client_util"
import { TextRenderer } from "../text/text_renderer"
import { blendColor, drawFlareGlow, FLARE_COLOR, FLARE_MS, flareIntensity, flareScale } from "./flare"

// The shape every toggle in the top-right HUD row shares: a small pill carrying a glyph and a
// one-word label, on a dark translucent plate with a thin light rim.
//
// It exists as one object because there are several of these sitting in a row (players, items, map,
// say) and they have to read as one set of controls rather than badges that happen to be near each
// other. The players chip is not built on it -- it carries a count whose width moves the row -- but
// it is drawn from the same constants, which live here so the two cannot drift apart.
export const CHIP_PAD_X = 6
export const CHIP_PAD_Y = 4
const CORNER = 5
const GLYPH_SIZE = 11
const GAP = 4

export const CHIP_PANEL_COLOR = 0x05070a
export const CHIP_PANEL_ALPHA = 0.59
export const CHIP_PANEL_ALPHA_HOVER = 0.82
export const CHIP_BORDER_ALPHA = 0.36
export const CHIP_BORDER_ALPHA_HOVER = 0.75
export const CHIP_LABEL_COLOR = 0xcfd8dc
export const CHIP_LABEL_COLOR_HOVER = 0xffffff

// How a chip that is ALSO a toggle shows which way it is set. Only the glyph's opacity changes: the
// plate is already a dark pill with a thin rim and has no room for a second signal, and dimming the
// icon is the one cue that reads as "off" at this size without a word for it. A chip that is just a
// button (the inventory one, the chat one) never leaves the "on" value, so it looks exactly as it did.
const GLYPH_ALPHA_ON = 1
const GLYPH_ALPHA_OFF = 0.4

// How far proud of the plate the tap/click target reaches. These chips are small on any screen and
// genuinely small under a thumb, and each is the only way to whatever it opens, so the target is
// grown rather than the pill -- a bigger pill would eat the corner the map itself wants.
const HIT_PAD = isMobile() ? 10 : 3

export interface HudChipProps {
    glyph: string
    label: string
    onClick: Callback
}

export class HudChip extends Container {
    // Everything visible hangs off `body` rather than off the chip itself, so a flare's pop can
    // scale it about its own middle while the chip's x/y -- what layoutChipRow sets, and what the
    // panels beneath stack against -- stay exactly where they were put. (Same arrangement as
    // ember_players_chip.ts, which flared first.)
    private body = new Container()
    private glow = new Graphics()
    protected plate = new Graphics()
    private glyph: Text
    private label: Text
    private hovered = false
    // Whether what this chip toggles is currently showing. Chips that toggle nothing stay true and
    // draw as they always have.
    private active = true
    // Time left in the "look at me" flare, or 0 at rest -- which is the usual case: a chip only
    // flares when something it carries happened unasked (a line of chat arriving). At rest the glow
    // draws nothing and the pop is exactly 1, so a settled chip costs what it always did.
    private flareMs = 0

    constructor({ glyph, label, onClick }: HudChipProps) {
        super()

        // No tint on the emoji -- it carries its own colours.
        this.glyph = new TextRenderer().fontSize(GLYPH_SIZE).render(glyph) as Text
        this.label = new TextRenderer().size("tiny").render(label) as Text

        this.body.addChild(this.glow, this.plate, this.glyph, this.label)
        this.addChild(this.body)
        this.layout()
        this.draw()

        this.interactive = true
        this.cursor = "pointer"
        this.on("mouseover", () => {
            this.hovered = true
            this.draw()
        })
        this.on("mouseout", () => {
            this.hovered = false
            this.draw()
        })
        this.on("click", onClick)
        this.on("tap", onClick)

        // Every chip in this row is put on screen by whatever owns it, once it has something to
        // say. None of them should appear merely by being constructed.
        this.visible = false
    }

    // Both sit on a shared centre line, so the glyph and the text don't stagger.
    private layout = () => {
        this.glyph.x = CHIP_PAD_X
        this.label.x = CHIP_PAD_X + this.glyph.width + GAP
        const h = Math.max(this.glyph.height, this.label.height)
        this.glyph.y = CHIP_PAD_Y + (h - this.glyph.height) / 2
        this.label.y = CHIP_PAD_Y + (h - this.label.height) / 2
        // Pivot and position on the same point: the body then occupies its old box at rest and
        // swells symmetrically out of the middle when a flare's pop scales it.
        const centre = { x: this.getWidth() / 2, y: this.getHeight() / 2 }
        this.body.pivot.set(centre.x, centre.y)
        this.body.position.set(centre.x, centre.y)
        this.hitArea = new Rectangle(
            -HIT_PAD,
            -HIT_PAD,
            this.getWidth() + HIT_PAD * 2,
            this.getHeight() + HIT_PAD * 2,
        )
    }

    // Start a flare, unless one is already running: that is what rate-limits the thing. A busy
    // conversation would otherwise restart the flare on every line and leave the corner blinking
    // for as long as people are talking.
    flash = () => {
        if (this.flareMs > 0) {
            return
        }
        this.flareMs = FLARE_MS
        this.draw()
    }

    isFlashing = (): boolean => this.flareMs > 0

    // Say which way the thing this chip toggles is currently set. Idempotent, because the owner
    // re-asserts it from its layout pass rather than only on a change.
    setActive = (active: boolean) => {
        if (this.active === active) {
            return
        }
        this.active = active
        this.draw()
    }

    isActive = (): boolean => this.active

    // Drain the flare. Only repaints while one is running, so a chip that has settled costs nothing
    // per frame.
    animate = (dtMs: number) => {
        if (this.flareMs <= 0) {
            return
        }
        this.flareMs = Math.max(0, this.flareMs - dtMs)
        this.draw()
    }

    protected draw = () => {
        const hot = flareIntensity(this.flareMs)
        const w = this.getWidth()
        const h = this.getHeight()
        // The row sits 6px off the top of the screen, so the halo is kept short: anything taller is
        // spent off-canvas.
        drawFlareGlow(this.glow, { remainingMs: this.flareMs, width: w, height: h, corner: CORNER })
        this.plate.clear()
        this.plate.beginFill(
            blendColor(CHIP_PANEL_COLOR, FLARE_COLOR, hot * 0.85),
            (this.hovered ? CHIP_PANEL_ALPHA_HOVER : CHIP_PANEL_ALPHA) + hot * (1 - CHIP_PANEL_ALPHA),
        )
        // The rim stays near white at the peak rather than going orange with the fill: a bright
        // edge around a hot plate is what makes the thing look lit instead of merely recoloured.
        this.plate.lineStyle(
            1 + hot * 1.5,
            blendColor(0xffffff, FLARE_COLOR, hot * 0.4),
            Math.min(1, (this.hovered ? CHIP_BORDER_ALPHA_HOVER : CHIP_BORDER_ALPHA) + hot),
        )
        this.plate.drawRoundedRect(0, 0, w, h, CORNER)
        this.plate.endFill()
        this.label.tint = blendColor(this.hovered ? CHIP_LABEL_COLOR_HOVER : CHIP_LABEL_COLOR, 0xffffff, hot)
        // Hovering an "off" toggle brings its glyph most of the way back up, so the chip previews what
        // the click is about to turn on.
        this.glyph.alpha = this.active || this.hovered ? GLYPH_ALPHA_ON : GLYPH_ALPHA_OFF
        this.body.scale.set(flareScale(this.flareMs))
    }

    // Measured off the content rather than getBounds(), so the plate can be drawn to the size the
    // caller is about to position against without a chicken-and-egg on bounds -- and so the row's
    // layout is the chip's RESTING size, unmoved by a flare in progress.
    getWidth = (): number => CHIP_PAD_X * 2 + this.glyph.width + GAP + this.label.width
    getHeight = (): number => CHIP_PAD_Y * 2 + Math.max(this.glyph.height, this.label.height)
}
